/**
 * Iter66 — Public certificate verification page.
 *
 * Anyone can paste an Audit ID and receive a verifier-friendly response:
 *   • valid + project name + company + tier + scores + delivered date
 *   • methodology version (so reviewers can pin the rules used)
 *   • OR an explicit "no QorTrace audit matches this ID" outcome
 *
 * The page is intentionally simple — single input, big result card,
 * helpful empty state. No auth. Rate-limited backend-side.
 */
import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Search, ShieldCheck, ShieldAlert, Loader2, ArrowRight,
  ScrollText, ExternalLink, Copy, Check, Download,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { trackEvent } from "../lib/analytics";


const BACKEND = process.env.REACT_APP_BACKEND_URL;


export default function VerifyPage() {
  useDocumentTitle("Verify a QorTrace Audit");
  useEffect(() => {
    trackEvent("verify_loaded", { surface: "public-verify" });
  }, []);
  const { auditId: routeId } = useParams();
  const [auditId, setAuditId] = useState(routeId || "");
  const [result, setResult]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  const autoRanRef = useRef(false);

  const runVerify = async (id) => {
    const trimmed = (id || "").trim();
    if (!trimmed) {
      setErr("Paste a QorTrace audit ID to verify.");
      return;
    }
    setErr(""); setResult(null); setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND}/api/audit/verify`,
        { audit_id: trimmed });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  // Auto-verify when arriving via /verify/:auditId — links shared in
  // questionnaires & vendor pages drop straight into a result state.
  useEffect(() => {
    if (routeId && !autoRanRef.current) {
      autoRanRef.current = true;
      runVerify(routeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const submit = (e) => {
    e?.preventDefault();
    runVerify(auditId);
  };

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[900px] mx-auto px-6 sm:px-8 py-12"
           data-testid="verify-page">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={14} style={{ color: "var(--qt-cyan)" }} />
          <div className="qt-label" style={{ letterSpacing: "0.18em" }}>
            CERTIFICATE VERIFICATION
          </div>
        </div>
        <h1 className="qt-h2 text-3xl sm:text-4xl"
            data-testid="verify-title">
          Validate a QorTrace audit
        </h1>
        <p className="mt-3 max-w-2xl"
           style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
          Confirm whether an audit ID corresponds to a real QorTrace
          delivery. Auditors, procurement reviewers, and security teams
          can use this page to validate any cert claimed in a vendor
          questionnaire, pitch deck, or marketing site. Source of truth
          is QorTrace's database — not the cert image itself.
        </p>

        <form onSubmit={submit} className="qt-card mt-6"
              data-testid="verify-form"
              style={{ padding: 22,
                       background: "rgba(4,9,20,0.55)",
                       borderColor: "var(--qt-border)" }}>
          <label className="qt-label block mb-2"
                 style={{ letterSpacing: "0.16em" }}>
            AUDIT ID
          </label>
          <div className="flex gap-2">
            <input value={auditId}
                   onChange={(e) => setAuditId(e.target.value)}
                   data-testid="verify-input"
                   placeholder="e.g. demo-featured-001-babb7d"
                   className="qt-input"
                   style={{ flex: 1 }} />
            <button type="submit"
                    disabled={busy || !auditId.trim()}
                    data-testid="verify-submit"
                    className="qt-btn qt-btn-primary inline-flex items-center gap-2"
                    style={{ fontSize: 13, padding: "10px 18px" }}>
              {busy ? <Loader2 size={13} className="animate-spin" />
                    : <Search size={13} />}
              {busy ? "Checking…" : "Verify"}
            </button>
          </div>
          {err && (
            <div className="qt-mono text-[12px] mt-3"
                 data-testid="verify-error"
                 style={{ color: "#FF6E6E", letterSpacing: "0.04em" }}>
              {err}
            </div>
          )}
        </form>

        {result && <ResultCard r={result} />}

        {/* Sub-bar: where else to verify */}
        <div className="mt-12 text-center qt-mono text-[11px]"
             data-testid="verify-other-surfaces"
             style={{ color: "var(--qt-text-3)",
                      letterSpacing: "0.06em", lineHeight: 1.6 }}>
          You can also verify a cert from the audit page banner, the
          methodology footer, or the contract page hero. Customers can
          download their own un-watermarked copy from{" "}
          <Link to="/account/audits"
                style={{ color: "var(--qt-cyan)",
                         textDecoration: "underline" }}>
            their account dashboard
          </Link>.
        </div>
      </div>
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Verifier result card
// ---------------------------------------------------------------------------
function ResultCard({ r }) {
  const valid = !!r.valid;
  const accent = valid ? "#1FDD83" : "#FF6E6E";
  const Icon   = valid ? ShieldCheck : ShieldAlert;
  return (
    <div className="qt-card mt-6"
         data-testid={valid ? "verify-result-valid" : "verify-result-invalid"}
         style={{ padding: 22,
                  background: "rgba(4,9,20,0.7)",
                  borderColor: accent + "55",
                  borderLeft: `3px solid ${accent}` }}>
      <div className="flex items-center gap-2.5 mb-1">
        <Icon size={18} style={{ color: accent }} />
        <div className="qt-mono"
             style={{ color: accent, fontWeight: 700,
                      letterSpacing: "0.10em" }}>
          {valid ? "VALID · QORTRACE AUDIT FOUND"
                 : "NOT FOUND · NO MATCHING AUDIT"}
        </div>
      </div>
      {!valid && (
        <p className="text-sm mt-3"
           style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          {r.reason || "No matching audit was found in the QorTrace registry."}{" "}
          If you received this audit ID from a vendor, ask them for the
          canonical certificate URL{" "}
          <code className="qt-mono text-[12px]"
                style={{ color: "var(--qt-cyan)" }}>
            qortrace.com/api/audit/&lt;ID&gt;/certificate.html
          </code>{" "}— forged certs typically can't produce one.
        </p>
      )}
      {valid && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <Field label="PROJECT" value={r.project_name} bold />
            <Field label="TIER"
                   value={(r.tier || "").toUpperCase().replace("_", " ")} />
            <Field label="DELIVERED"
                   value={r.delivered_at?.slice(0, 10) || "—"} />
            <Field label="SECURITY SCORE"
                   value={`${r.security_score ?? "—"} / 100`}
                   accent="var(--qt-cyan)" />
            <Field label="TRUST SCORE"
                   value={`${r.trust_score ?? "—"} / 100`}
                   accent="var(--qt-cyan)" />
            <Field label="AUDIT ID"
                   value={r.audit_id_short} mono />
          </div>
          {r.severity_summary && (
            <div className="mt-4 flex flex-wrap gap-2"
                 data-testid="verify-severity">
              {Object.entries(r.severity_summary).map(([sev, n]) => (
                <span key={sev}
                      className="qt-mono text-[10px] px-2.5 py-1 rounded"
                      style={{ color: "var(--qt-text-2)",
                               border: "1px solid var(--qt-border)",
                               letterSpacing: "0.16em" }}>
                  {sev.toUpperCase()} · {n}
                </span>
              ))}
            </div>
          )}
          {r.methodology_version && (
            <div className="qt-mono text-[11px] mt-4"
                 style={{ color: "var(--qt-text-3)",
                          letterSpacing: "0.06em" }}>
              METHODOLOGY · {r.methodology_version}
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={r.certificate_url}
               target="_blank" rel="noopener noreferrer"
               data-testid="verify-cert-link"
               className="qt-btn qt-btn-secondary inline-flex items-center gap-2"
               style={{ fontSize: 12, padding: "7px 16px" }}>
              View canonical certificate
              <ExternalLink size={11} />
            </a>
            {r.report_pdf_url && (
              <a href={r.report_pdf_url}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="verify-pdf-link"
                 className="qt-btn qt-btn-secondary inline-flex items-center gap-2"
                 style={{ fontSize: 12, padding: "7px 16px" }}>
                <Download size={11} />
                Download report (PDF)
              </a>
            )}
            <button data-testid="verify-copy-url"
                    onClick={() => {
                      const url = `${window.location.origin}/verify/${r.audit_id}`;
                      navigator.clipboard.writeText(url);
                    }}
                    className="qt-btn qt-btn-ghost inline-flex items-center gap-2"
                    style={{ fontSize: 12, padding: "7px 16px" }}>
              <Copy size={11} />
              Copy verify URL
            </button>
            <Link to="/methodology"
                  data-testid="verify-methodology-link"
                  className="qt-btn qt-btn-ghost inline-flex items-center gap-2"
                  style={{ fontSize: 12, padding: "7px 16px" }}>
              <ScrollText size={11} />
              Methodology
            </Link>
          </div>

          {/* Iter67 — shareable verify badge. One-click snippets so any
              project can paste a "Verified by QorTrace" link into their
              README, docs, or vendor questionnaire. Every paste = a
              first-party backlink to /verify/:id, our procurement-side
              top-of-funnel surface. */}
          <ShareVerifyBadge auditId={r.audit_id}
                             score={r.security_score}
                             projectName={r.project_name} />
        </>
      )}
    </div>
  );
}

function Field({ label, value, bold, mono, accent }) {
  return (
    <div>
      <div className="qt-mono text-[10px]"
           style={{ color: "var(--qt-text-3)",
                    letterSpacing: "0.16em" }}>
        {label}
      </div>
      <div className={mono ? "qt-mono" : ""}
           style={{ color: accent || (bold ? "#fff" : "var(--qt-text-1)"),
                    fontWeight: bold ? 700 : 600,
                    fontSize: bold ? 16 : 14,
                    marginTop: 3 }}>
        {value || "—"}
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Iter67 — shareable verify badge
//
// Three copy formats (Markdown / HTML / URL) that each link to /verify/:id
// — the first-party verification surface. Customers paste these into their
// README, docs, or vendor questionnaire response; every paste is a backlink
// + lead funnel for QorTrace.
// ---------------------------------------------------------------------------
function ShareVerifyBadge({ auditId, score, projectName }) {
  const [tab, setTab] = useState("markdown");
  const [copiedKey, setCopiedKey] = useState("");

  // Iter68 — pin the verify URL to the production domain so customer
  // READMEs/docs always hit qortrace.com, regardless of where they were
  // viewing the result (preview / staging / dev). Override via env var
  // for self-hosted or alternative-domain deployments.
  const VERIFY_BASE = (process.env.REACT_APP_VERIFY_BASE_URL
                       || "https://qortrace.com").replace(/\/$/, "");
  const verifyUrl = `${VERIFY_BASE}/verify/${auditId}`;
  // Iter76 — OG-tagged share URL. Same canonical target as verifyUrl
  // (auto-redirects humans there via meta refresh + JS) but emits per-
  // audit OG meta tags so LinkedIn / X / Slack / iMessage previews
  // render the polished 1200×630 cyan-on-navy "Verified by QorTrace"
  // card instead of a generic site preview.
  const shareUrl  = `${VERIFY_BASE}/api/audit/${auditId}/share`;
  const ogImage   = `${VERIFY_BASE}/api/audit/${auditId}/og.png`;
  const embedUrl  = `${VERIFY_BASE}/api/audit/${auditId}/embed`;
  const embedSnippet =
    `<iframe src="${embedUrl}" width="360" height="100" frameborder="0" ` +
    `loading="lazy" title="Verified by QorTrace · ${(projectName || "smart contract").replace(/"/g, "&quot;")}" ` +
    `style="border:0;background:transparent;border-radius:8px;" ` +
    `allowtransparency="true"></iframe>`;
  // Shields.io renders a tiny SVG badge for any text. We use it as a visual
  // anchor on the customer's README — the click target is our /verify URL.
  const badgeImg =
    `https://img.shields.io/badge/QorTrace-Verified%20Audit%20${score ?? "—"}%2F100-00E5FF?style=flat-square&logo=shield&logoColor=040914`;
  const altText = `QorTrace · Verified Audit · ${(projectName || "smart contract")} · ${score ?? "—"}/100`;

  const markdown = `[![${altText}](${badgeImg})](${verifyUrl})`;
  const html =
    `<a href="${verifyUrl}" target="_blank" rel="noopener">\n` +
    `  <img src="${badgeImg}" alt="${altText}" />\n` +
    `</a>`;
  const urlOnly = verifyUrl;

  const TABS = [
    { id: "markdown", label: "Markdown", value: markdown,
      hint: "Paste into a GitHub README — readers click straight to /verify." },
    { id: "html",     label: "HTML",     value: html,
      hint: "Drop into a docs site, landing page, or Notion HTML block." },
    { id: "url",      label: "URL",      value: urlOnly,
      hint: "Send the raw verify URL in vendor questionnaires & emails." },
    { id: "social",   label: "Social",   value: shareUrl,
      hint: "Use this URL when sharing to LinkedIn / X / Slack — emits a polished 1200×630 OG card preview, then auto-redirects clickers to /verify." },
    { id: "embed",    label: "Embed",    value: embedSnippet,
      hint: "Drop this iframe onto your dApp landing page or vendor questionnaire — live, score-updating widget that auto-refreshes when you remediate findings." },
  ];
  const active = TABS.find((t) => t.id === tab) || TABS[0];

  const onCopy = async (key, value) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for non-secure contexts / headless test runners.
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1600);
    } catch (_) { /* clipboard unavailable — manual select still works */ }
  };

  return (
    <div className="mt-5 pt-5"
         data-testid="verify-share-badge"
         style={{ borderTop: "1px dashed var(--qt-border)" }}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={13} style={{ color: "var(--qt-cyan)" }} />
        <div className="qt-mono text-[10px]"
             style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em",
                      fontWeight: 700 }}>
          SHAREABLE VERIFY BADGE · ONE-CLICK EMBED
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <img src={badgeImg} alt={altText}
             data-testid="verify-share-preview"
             style={{ height: 22 }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.04em" }}>
          links to {verifyUrl}
        </span>
      </div>

      {/* Iter77 — live iframe preview when the Embed tab is active so
          customers can SEE what they're about to drop into their site. */}
      {tab === "embed" && (
        <div className="mb-3"
             data-testid="verify-share-embed-preview"
             style={{
               padding: 12,
               background: "rgba(0,0,0,0.45)",
               border: "1px dashed var(--qt-border)",
               borderRadius: 6,
             }}>
          <div className="qt-mono text-[10px] mb-2"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
            LIVE PREVIEW · UPDATES AS YOU REMEDIATE
          </div>
          <iframe src={embedUrl} width="360" height="100"
                  title="QorTrace verify badge live preview"
                  style={{ border: 0, background: "transparent",
                           borderRadius: 8, display: "block" }}
                  loading="lazy" />
        </div>
      )}

      <div className="flex items-center gap-1 mb-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} type="button"
                  data-testid={`verify-share-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="qt-mono text-[11px] px-3 py-1 rounded transition-colors"
                  style={{
                    background: tab === t.id ? "var(--qt-cyan)" : "transparent",
                    color: tab === t.id ? "#0A1020" : "var(--qt-text-2)",
                    border: tab === t.id
                      ? "1px solid var(--qt-cyan)"
                      : "1px solid var(--qt-border)",
                    letterSpacing: "0.10em", fontWeight: 700,
                  }}>
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="relative">
        <pre data-testid={`verify-share-snippet-${active.id}`}
             className="qt-mono text-[11px]"
             style={{
               margin: 0,
               padding: "0.85rem 3rem 0.85rem 0.85rem",
               background: "rgba(0,0,0,0.45)",
               border: "1px solid var(--qt-border)",
               borderRadius: 6,
               color: "#D6E0EE",
               whiteSpace: "pre-wrap",
               wordBreak: "break-all",
               lineHeight: 1.55,
               maxHeight: 140,
               overflow: "auto",
             }}>{active.value}</pre>
        <button type="button"
                onClick={() => onCopy(active.id, active.value)}
                data-testid={`verify-share-copy-${active.id}`}
                className="qt-btn qt-btn-secondary"
                style={{
                  position: "absolute", top: 8, right: 8,
                  fontSize: 11, padding: "0.3rem 0.55rem",
                }}>
          {copiedKey === active.id
            ? (<><Check size={11} /> Copied</>)
            : (<><Copy size={11} /> Copy</>)}
        </button>
      </div>
      <p className="text-[11px] mt-2" style={{ color: "var(--qt-text-3)" }}>
        {active.hint}
      </p>
    </div>
  );
}
